import { forwardRef, HtmlHTMLAttributes, memo, useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { usePrevious } from "../../../../utils";
import { Button, ButtonProps } from "../../../ui";
import { defaultLeadingZero, defaultShortcuts } from "./constants";
import { getCronStringFromValues, setValuesFromCronString } from "./converter";
import { DropdownsConfig, Hours, Minutes, MonthDays, Months, Period, WeekDays } from "./fields";
import { DEFAULT_LOCALE_EN } from "./locale";
import { AllowEmpty, ClockFormat, CronType, cronTypes, LeadingZero, Locale, OnError, PeriodType, periodTypes, SetValue, Shortcuts } from "./types";

import styles from "./styles.module.scss";

export type CronFieldProps = HtmlHTMLAttributes<HTMLDivElement> & {
  value?: string
  onChange?: SetValue
  humanizeLabels?: boolean
  humanizeValue?: boolean
  leadingZero?: LeadingZero
  defaultPeriod?: PeriodType
  disabled?: boolean
  isClearable?: boolean
  allowEmpty?: AllowEmpty
  shortcuts?: Shortcuts
  clockFormat?: ClockFormat
  clearButton?: boolean
  clearButtonProps?: Omit<ButtonProps, "onClick">
  clearButtonAction?: "empty" | "fill-with-every"
  displayError?: boolean
  onError?: OnError
  allowedDropdowns?: ReadonlyArray<CronType>
  allowedPeriods?: ReadonlyArray<PeriodType>
  dropdownsConfig?: DropdownsConfig
  locale?: Locale
}

export const CronField = memo(forwardRef<HTMLDivElement, CronFieldProps>((
  {
    clearButton = false,
    clearButtonAction = "fill-with-every",
    locale = DEFAULT_LOCALE_EN,
    value = "",
    defaultPeriod = "day",
    allowEmpty = "always",
    humanizeLabels = true,
    humanizeValue = false,
    disabled = false,
    leadingZero = defaultLeadingZero,
    shortcuts = defaultShortcuts,
    allowedDropdowns = cronTypes,
    allowedPeriods = periodTypes,
    className,
    onError,
    onChange,
    dropdownsConfig,
    clockFormat,
    clearButtonProps,
    ...props
  }: CronFieldProps, ref) => {
  const internalValueRef = useRef<string>(value);
  const defaultPeriodRef = useRef<PeriodType>(defaultPeriod);
  const [ period, setPeriod ] = useState<PeriodType | null>(null);
  const [ monthDays, setMonthDays ] = useState<number[]>([]);
  const [ months, setMonths ] = useState<number[]>([]);
  const [ weekDays, setWeekDays ] = useState<number[]>([]);
  const [ hours, setHours ] = useState<number[]>([]);
  const [ minutes, setMinutes ] = useState<number[]>([]);
  const [ valueCleared, setValueCleared ] = useState<boolean>(false);
  const previousValueCleared = usePrevious(valueCleared);

  useEffect(
    () => {
      setValuesFromCronString(
        value, onError, allowEmpty, internalValueRef, true, shortcuts,
        setMinutes, setHours, setMonthDays, setMonths, setWeekDays, setPeriod,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => {
      if (value !== internalValueRef.current) {
        setValuesFromCronString(
          value, onError, allowEmpty, internalValueRef, false, shortcuts,
          setMinutes, setHours, setMonthDays, setMonths, setWeekDays, setPeriod,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ value, internalValueRef, allowEmpty, shortcuts ],
  );

  useEffect(
    () => {
      // Only change the value if a user touched a field
      // and if the user didn't use the clear button
      if (
        (period || minutes || months || monthDays || weekDays || hours) &&
        !valueCleared &&
        !previousValueCleared
      ) {
        const selectedPeriod = period || defaultPeriodRef.current;
        const cron = getCronStringFromValues(
          selectedPeriod, months, monthDays, weekDays, hours, minutes, humanizeValue, dropdownsConfig,
        );
        onChange?.(cron, { selectedPeriod });
        internalValueRef.current = cron;
        onError?.(false);
      } else if (valueCleared) {
        setValueCleared(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ period, monthDays, months, weekDays, hours, minutes, humanizeValue, valueCleared, dropdownsConfig ],
  );

  const handleClear = useCallback(
    () => {
      setMonthDays([]);
      setMonths([]);
      setWeekDays([]);
      setHours([]);
      setMinutes([]);

      // When clearButtonAction is 'empty'
      let newValue = "";

      const newPeriod =
        period !== "reboot" && period ? period : defaultPeriodRef.current;

      if (newPeriod !== period) {
        setPeriod(newPeriod);
      }

      // When clearButtonAction is 'fill-with-every'
      if (clearButtonAction === "fill-with-every") {
        newValue = getCronStringFromValues(newPeriod);
      }

      onChange?.(newValue, { selectedPeriod: newPeriod });
      internalValueRef.current = newValue;

      setValueCleared(true);

      onError?.(allowEmpty === "never" && clearButtonAction === "empty");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ period, onChange, onError, clearButtonAction ],
  );

  const periodForRender = period || defaultPeriodRef.current;

  return (
    <div ref={ref} {...props} className={classNames(styles.root, className)}>
      {allowedDropdowns.includes("period") && (
        <Period
          {...dropdownsConfig?.period}
          defaultValue={periodForRender}
          value={periodForRender}
          onChange={setPeriod}
          shortcuts={shortcuts}
          allowedPeriods={allowedPeriods}
          locale={locale}
          disabled={dropdownsConfig?.period?.disabled ?? disabled}
        />
      )}
      {
        periodForRender !== "reboot" && (<>
          {
            (periodForRender === "year" && allowedDropdowns.includes("months")) &&
            <Months
              {...dropdownsConfig?.months}
              value={months}
              onChange={setMonths}
              locale={locale}
              humanizeLabels={dropdownsConfig?.months?.humanizeLabels ?? humanizeLabels}
              disabled={dropdownsConfig?.months?.disabled ?? disabled}
            />
          }

          {
            ((periodForRender === "year" || periodForRender === "month") && allowedDropdowns.includes("month-days")) &&
            <MonthDays
              {...dropdownsConfig?.["month-days"]}
              value={monthDays}
              onChange={setMonthDays}
              weekDays={weekDays}
              locale={locale}
              disabled={dropdownsConfig?.["month-days"]?.disabled ?? disabled}
              leadingZero={dropdownsConfig?.["month-days"]?.leadingZero ?? leadingZero}
            />
          }

          {
            ((periodForRender === "year" || periodForRender === "month" || periodForRender === "week") && allowedDropdowns.includes("week-days")) &&
            <WeekDays
              {...dropdownsConfig?.["week-days"]}
              value={weekDays}
              onChange={setWeekDays}
              monthDays={monthDays}
              period={periodForRender}
              locale={locale}
              humanizeLabels={dropdownsConfig?.["week-days"]?.humanizeLabels ?? humanizeLabels}
              disabled={dropdownsConfig?.["week-days"]?.disabled ?? disabled}
            />
          }

          <div className={styles.timeBlock}>
            {
              (periodForRender !== "minute" && periodForRender !== "hour" && allowedDropdowns.includes("hours")) &&
              <Hours
                value={hours}
                onChange={setHours}
                locale={locale}
                disabled={dropdownsConfig?.hours?.disabled ?? disabled}
                leadingZero={dropdownsConfig?.hours?.leadingZero ?? leadingZero}
                clockFormat={clockFormat}
              />
            }

            {
              (periodForRender !== "minute" && allowedDropdowns.includes("minutes")) &&
              <Minutes
                value={minutes}
                onChange={setMinutes}
                period={periodForRender}
                locale={locale}
                disabled={dropdownsConfig?.minutes?.disabled ?? disabled}
                leadingZero={dropdownsConfig?.minutes?.leadingZero ?? leadingZero}
                clockFormat={clockFormat}
              />
            }
          </div>
        </>)
      }

      {
        clearButton &&
        <Button
          disabled={disabled}
          buttonType="secondary"
          type="button"
          {...clearButtonProps}
          onClick={handleClear}
        >
          {locale.clearButtonText}
        </Button>
      }
    </div>
  );
}));

export default CronField;
